import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect, PromiseState } from 'react-refetch';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { FormGroup, Radio } from 'react-bootstrap';

import PromiseStateContainer from '../../apiAccess/PromiseStateContainer';
import BASE_URL from '../../apiAccess/urls';
import { mediaQuery, sortFcdbFoodId, replaceNaN,
  linkFoodDetail, sortNumberString, empty_field, empty_key_message} from '../../util/util';
import Buttons from '../../util/Buttons';

import './MultiNutrientResult.css';

const tableDefault = {
  page: 1,
  size: 25,
  sort: 'nutrient1',
  order: 'desc',
};

// this is what the API will return if there is no data
//const empty_data = "■"


/**
 * Concerns of this component:
 *   fetch /api/foodsort/{code}[?group=A-Z]
 *   create table data
 *   render search result table
**/
class MultiNutrientResult extends Component {
  constructor(props) {
    super(props);

    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.renderShowsTotal = this.renderShowsTotal.bind(this);
    this.renderOnFulfillment = this.renderOnFulfillment.bind(this);
  }

  handlePageChange(page, size) {
    const newPage = (page === tableDefault.page) ? '' : page;
    const newSize = (size === tableDefault.size) ? '' : size;
    this.props.handlePageChange(newPage, newSize);
  }

  handleSortChange(sortName, sortOrder) {
    const isDefault = sortName === tableDefault.sort && sortOrder === tableDefault.order;
    const newSortName = isDefault ? '' : sortName;
    const newSortOrder = isDefault ? '' : sortOrder;
    this.props.handleSortChange(newSortName, newSortOrder);
  }

  getNumberToShow(foodComponent) {
    if (!foodComponent) { return null; }
    return this.props.per100g ? foodComponent.value : foodComponent.amount;
  }

  getComponentUnit(foodComponent) {
    //if (!foodComponent.num_value) { return null; }
    if (!foodComponent) { return null; }
    const unit = foodComponent.unit_abbr;
    return unit ? unit.replace(/\/100g/, '') : '';

  }

  renderShowsTotal(start, to, total, components) {
    start = start.toLocaleString('en');
    to = to.toLocaleString('en');
    total = total.toLocaleString('en');
    return (
      <div className='showsTotal'>
        <span>
          Showing {start}-{to} of {total} foods
        </span>
        <span className='containing'>
          containing
          {components.reduce((result, component) => {
            result += result.length > 0 ? '; ' : ' '; // add separator between nutrient names
            result += component.display_name;
            return result;
          }, '')}
        </span>
        <form>
          <FormGroup>
            <span>measured</span>
            <Radio inline name='base'
              checked={!this.props.per100g}
              onChange={() => this.props.handlePerChange(false)}>
              per serve</Radio>
            <Radio inline name='base'
              checked={this.props.per100g}
              onChange={() => this.props.handlePerChange(true)}>
              per 100 g</Radio>
          </FormGroup>
        </form>
        <div>{ empty_key_message }</div>
      </div>
    );
  }

  render() {
    const { codes, foodmultisortFetch, unitFetch } = this.props;
    if(!codes) { return null; }

    return (
      <PromiseStateContainer
        ps={PromiseState.all([foodmultisortFetch, unitFetch])}
        onFulfillment={this.renderOnFulfillment}
      />
    );
  }

  renderOnFulfillment([results, units]) {
    const { codes, per100g, groups, group } = this.props;
    const components = codes.map(
      code => this.props.components.find(component => component.code === code)
    );
    // to get unit of the component
    components.forEach(component => {
      const unit = units.find(unit => unit._id === component.unit_id);
      component.unit_abbr = unit.abbreviation.replace(/\/100g/, '');
    });

    // create table data
    const tableData = results.foods.map(food => {
      const foodComponentsOrdered = codes.map(code => food.components[code]);
      return {
        shortname: food.shortname,
        recordid: food.food_record_id,
        serving_size_value: food.serving_size_value,
        serving_size_unit: food.serving_size_unit,
        nutrient1: this.getNumberToShow(foodComponentsOrdered[0]),
        nutrient2: this.getNumberToShow(foodComponentsOrdered[1]),
        nutrient3: this.getNumberToShow(foodComponentsOrdered[2]),
        nutrient1_unit: this.getComponentUnit(foodComponentsOrdered[0]),
        nutrient2_unit: this.getComponentUnit(foodComponentsOrdered[1]),
        nutrient3_unit: this.getComponentUnit(foodComponentsOrdered[2]),
      };
    });

    // props validation. invalid => default
    const sizePerPage = this.props.size || tableDefault.size;
    const page = (this.props.page && results.foods.length > (this.props.page - 1) * sizePerPage)
      ? this.props.page : tableDefault.page;
    const sortColumns = [
      'shortname', 'recordid', 'serving_size_value',
      'nutrient1', 'nutrient2', 'nutrient3',
    ];
    const sortName = sortColumns.includes(this.props.sort) ? this.props.sort : tableDefault.sort;
    const sortOrder = this.props.order || tableDefault.order;

    // CSV configuration
    let csvHeader = [
      {label: 'Food', key: 'shortname'},
      {label: 'FCDB Food ID', key: 'recordid'},
      {label: 'Serving Size', key: 'serving_size_value'},
      {label: 'Serving Size Unit', key: 'serving_size_unit'},
    ];
    components.forEach((component, i) => {
      csvHeader = csvHeader.concat([
        {label: component.display_name || '', key: `nutrient${i + 1}`},
        {label: (component.display_name || '') + ' Unit', key: `nutrient${i + 1}_unit`}
      ]);
    });

    const csvData = tableData.slice().sort((a, b) => {
      const numA = replaceNaN(Number(a.nutrient1));
      const numB = replaceNaN(Number(b.nutrient1));
      return numB - numA;
    });

    const csvFoodNames = components.map((component, i) => {
      return `${i + 1}) ` + (component.display_name.length > 30
        ? component.display_name.slice(0, 27) + '...'
        : component.display_name);
    }).join(' ');
    const groupObject = groups.find(aGroup => aGroup.code === group);
    const csvGroupName = groupObject ? groupObject.display_label : 'All Groups';
    const csvPerServe = per100g ? 'per100g' : 'perServe';
    const csvFilename = `Multi-nutrient Search Result--${csvFoodNames}--in ${csvGroupName}--${csvPerServe}.csv`;

    // table options
    const options = {
      noDataText: 'No food was found',
      paginationShowsTotal: (start, to, total) => this.renderShowsTotal(start, to, total, components),
      paginationSize: mediaQuery(3, 5),
      sizePerPage: sizePerPage,
      sizePerPageList: [10, 25, 50, 100],
      page: page,
      onPageChange: this.handlePageChange,
      sortName: sortName,
      sortOrder: sortOrder,
      onSortChange: this.handleSortChange,
    };

    const alignCenter = {
      thStyle: {textAlign: 'center'},
      tdStyle: {textAlign: 'center'},
    };

    return (
      <div className='MultiNutrientResult'>
        <Buttons
          csvHeader={csvHeader}
          csvData={csvData}
          filename={csvFilename}
        />

        <BootstrapTable
          data={tableData}
          hover
          condensed
          bordered={false}
          pagination
          options={options}
        >

          <TableHeaderColumn
            dataField='shortname'
            dataFormat={linkFoodDetail}
            dataSort
          >
          FOOD</TableHeaderColumn>

          <TableHeaderColumn
            isKey
            dataField='recordid'
            dataFormat={linkFoodDetail}
            dataSort
            sortFunc={sortFcdbFoodId}
            {...alignCenter}
          >
          FCDB FOOD ID</TableHeaderColumn>

          <TableHeaderColumn
            dataField='serving_size_value'
            dataFormat={(cell, row) => `${cell} ${row.serving_size_unit}`}
            dataSort
            {...alignCenter}
          >
          SERVING SIZE OR CSM</TableHeaderColumn>

          <TableHeaderColumn
            dataField='nutrient1'
            dataFormat={cell => `${cell} ${cell !== empty_field ? components[0].unit_abbr : ''}`}
            dataSort
            sortFunc={sortNumberString}
            {...alignCenter}
          >
            {components[0].display_name.toUpperCase()}</TableHeaderColumn>

          {components[1] &&
            <TableHeaderColumn
              dataField='nutrient2'
              dataFormat={cell => `${cell} ${cell !== empty_field ? components[1].unit_abbr : ''}`}
              dataSort
              sortFunc={sortNumberString}
              {...alignCenter}
            >
              {components[1].display_name.toUpperCase()}</TableHeaderColumn>
          }
          {components[2] &&
            <TableHeaderColumn
              dataField='nutrient3'
              dataFormat={cell => `${cell} ${cell !== empty_field ? components[2].unit_abbr : ''}`}
              dataSort
              sortFunc={sortNumberString}
              {...alignCenter}
            >
              {components[2].display_name.toUpperCase()}</TableHeaderColumn>
          }
        </BootstrapTable>
      </div>
    );
  }
}

MultiNutrientResult.propTypes = {
  foodmultisortFetch: PropTypes.instanceOf(PromiseState),
  unitFetch: PropTypes.instanceOf(PromiseState),
  handlePageChange: PropTypes.func.isRequired,
  handleSortChange: PropTypes.func.isRequired,
  handlePerChange: PropTypes.func.isRequired,
  components: PropTypes.array.isRequired,
  groups: PropTypes.array.isRequired,
  codes: PropTypes.array,
  group: PropTypes.string,
  page: PropTypes.number,
  size: PropTypes.number,
  sort: PropTypes.string,
  order: PropTypes.string,
  per100g: PropTypes.bool.isRequired,
};

const fetch = props => {
  if (!props.codes) { return null; }
  const codes = props.codes.join(',');
  const group = props.group ? `?group_code=${props.group}` : '';
  return {
    foodmultisortFetch: `${BASE_URL}/foodmultisort/${codes}${group}`,
    unitFetch: `${BASE_URL}/unit`,
  };
};

export default connect(fetch)(MultiNutrientResult);



// WEBPACK FOOTER //
// ./src/components/search/multiNutrient/MultiNutrientResult.js